import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, AlertTriangle, CheckCircle, XCircle, Scale } from "lucide-react";

const TermsOfUse = () => {
  const lastUpdated = "January 10, 2026";

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 md:py-12" data-testid="terms-of-use-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <Card className="shadow-lg">
          <CardHeader className="border-b">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-[#2c3e50]" />
              <div>
                <CardTitle className="text-3xl font-bold text-[#2c3e50] font-['Manrope']">
                  Terms of Use
                </CardTitle>
                <p className="text-[#546e7a]">Last updated: {lastUpdated}</p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-6 md:p-8 space-y-8">
            {/* Acceptance */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Acceptance of Terms
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                By accessing and using the Protect Elders from Fraud (PEFF) platform, you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services. These terms apply to all users of the platform, including visitors, victims filing reports, and administrators.
              </p>
            </section>

            {/* User Responsibilities */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-[#27ae60]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  User Responsibilities
                </h2>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <p className="text-base text-[#2c3e50] mb-4 font-semibold">
                  By using PEFF, you agree to:
                </p>
                <ul className="space-y-2 text-[#546e7a]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60]">✓</span>
                    Provide accurate and truthful information in all reports
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60]">✓</span>
                    Upload only genuine documents and evidence
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60]">✓</span>
                    Keep your Case ID confidential and secure
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60]">✓</span>
                    Cooperate with verification requests if needed
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#27ae60]">✓</span>
                    Use the platform only for legitimate fraud reporting purposes
                  </li>
                </ul>
              </div>
            </section>

            {/* Prohibited Activities */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <XCircle className="w-6 h-6 text-[#e74c3c]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  Prohibited Activities
                </h2>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-base text-[#e74c3c] mb-4 font-semibold">
                  The following activities are strictly prohibited:
                </p>
                <ul className="space-y-2 text-[#2c3e50]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#e74c3c]">✗</span>
                    Filing false or fabricated fraud reports
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#e74c3c]">✗</span>
                    Submitting fake FIR numbers or forged documents
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#e74c3c]">✗</span>
                    Impersonating another person or victim
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#e74c3c]">✗</span>
                    Using the platform to harass or defame others
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#e74c3c]">✗</span>
                    Attempting to access other users' data without authorization
                  </li>
                </ul>
              </div>
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#f39c12] flex-shrink-0 mt-0.5" />
                  <p className="text-base text-[#2c3e50]">
                    <strong>Warning:</strong> False reporting of FIR numbers or fraudulent claims will lead to immediate account suspension and may result in legal action under relevant Indian laws.
                  </p>
                </div>
              </div>
            </section>

            {/* Accuracy of Information */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Accuracy of Information
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                Users are solely responsible for the accuracy of the information provided. PEFF does not verify the authenticity of all claims but reserves the right to reject or remove reports that appear fraudulent, incomplete, or violate these terms. We may request additional documentation to verify claims.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Scale className="w-6 h-6 text-[#2c3e50]" />
                <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope']">
                  Limitation of Liability
                </h2>
              </div>
              <div className="bg-slate-50 rounded-lg p-6">
                <p className="text-base text-[#546e7a] leading-relaxed mb-4">
                  PEFF is provided "as is" without warranties of any kind. We do not guarantee:
                </p>
                <ul className="list-disc list-inside text-[#546e7a] space-y-2">
                  <li>Recovery of lost funds or successful case resolution</li>
                  <li>Accuracy of status updates or investigation timelines</li>
                  <li>Uninterrupted or error-free service availability</li>
                  <li>That all information on the platform is current or complete</li>
                </ul>
                <p className="text-base text-[#546e7a] leading-relaxed mt-4">
                  PEFF shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of our platform.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Intellectual Property
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                All content on PEFF, including text, graphics, logos, and software, is the property of PEFF or its licensors and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Modifications to Terms
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                PEFF reserves the right to modify these Terms of Use at any time. Changes will be posted on this page with an updated revision date. Your continued use of the platform after any changes constitutes acceptance of the new terms.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Governing Law
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                These Terms of Use shall be governed by and construed in accordance with the laws of India. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in New Delhi, India.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-slate-50 rounded-lg p-6">
              <h2 className="text-xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                Questions?
              </h2>
              <p className="text-base text-[#546e7a] leading-relaxed">
                If you have any questions about these Terms of Use, please contact us at{" "}
                <a href="mailto:legal@peff.org" className="text-[#2c3e50] font-semibold underline">
                  legal@peff.org
                </a>
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfUse;
